import React, { useEffect, useState } from "react";
import { ReactSortable } from "react-sortablejs";
import GlobalLoader from "../shared/GlobalLoader";
import { useLoading } from "../../context/LoadingContext";
import axios from "axios";
import { useLocation, useNavigate } from "react-router-dom";
import SuccessPopup from "../shared/SuccessPopup";

type Widget = {
  id: string;
  title: string;
  subtitle: string;
};

const UserPreferenceWidgets = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const pageId = location.state?.pageId;
  const pageTitle = location.state?.pageTitle;
  const { isLoading, setLoading } = useLoading();
  const baseUrl = process.env.REACT_APP_BASE_URL;
  const userDetails = JSON.parse(localStorage.getItem("userDetails") || "");
  const accessToken = sessionStorage.getItem("accessToken");
  const headers = accessToken ? { Authorization: accessToken } : {};
  const [allWidgets, setAllWidgets] = useState<Widget[]>([]);
  const [selectedWidgets, setSelectedWidgets] = useState<Widget[]>([]);
  const [errorMessage, setErrorMessage] = useState(false);
  const [showSuccessPopup, setShowSuccessPopup] = useState({
    type: "",
    show: false,
    message: "",
  });

  const fetchUserPreferences = async () => {
    setLoading(true);
    let response: any;
    try {
      if (!pageId) {
        response = await axios.get(
          `${baseUrl}/user/${userDetails?.id}/preferences`,
          { headers }
        );
      } else {
        response = await axios.get(
          `${baseUrl}/user/${userDetails?.id}/page/${pageId}/preferences`,
          { headers }
        );
      }

      let selectWidgets: any = {};
      if (response?.data?.preferences) {
        try {
          selectWidgets = JSON.parse(response?.data?.preferences);
        } catch (error) {
          console.error("Failed to parse user preferences JSON", error);
        }
      }
      const selectedWidgets = selectWidgets?.selectedWidgets || [];
      setSelectedWidgets(selectedWidgets);
      fetchAllowedWidgets(selectedWidgets);
      setLoading(false);
    } catch (error) {
      console.error("Failed to fetch user preferences", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchAllowedWidgets = async (selectedWidgets: any) => {
    setLoading(true);
    try {
      const response = await axios.get(
        `${baseUrl}/user/${userDetails?.id}/allowedWidgets`,
        { headers }
      );
      const allowedWidgets = response?.data?.initialDashboardAllowedWidgets || [];
      if (allowedWidgets?.length === 0) {
        setErrorMessage(true);
      }
      else {
        setErrorMessage(false);
      }

      let filteredWidgets = allowedWidgets;
      if (selectedWidgets.length > 0) {
        filteredWidgets = allowedWidgets.filter(
          (widget: any) =>
            !selectedWidgets.some((selected: any) => selected.id === widget.id)
        );
      }
      setAllWidgets(filteredWidgets);

      setLoading(false);
    } catch (error) {
      console.error("Failed to fetch allowed widgets", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUserPreferences();
  }, []);


  const renderWidgetCard = (widget: any) => (
    <li key={widget.id} className="theme-container mx-2 rounded" style={{ listStyleType: "none" }}>
      <div
        className="d-flex rounded align-items-center flex-row mt-4 p-3 shadow-sm"
        draggable="true"
        data-selected={widget.id}
      >
        {/* <i className="fa-solid fa-chart-simple fs-1 px-4 py-3 widget-icon"></i> */}
        <i className="bi bi-bar-chart-fill widget-icon fnt18 py-2 px-3"></i>
        <div className="d-block mx-4">
          <div className="fnt16 fw-600 mb-0">{widget.title}</div>
          <div className="fnt12 text-secondary">{widget.subtitle}</div>
        </div>
      </div>
    </li>
  );

  const handleSave = async () => {
    const accessToken = sessionStorage.getItem("accessToken");
    const headers = accessToken ? { Authorization: `${accessToken}` } : {};
    try {
      setLoading(true);
      const response = await axios.post(
        `${baseUrl}/user/${userDetails?.id}/page/${pageId}/preferences`,
        {
          pageId,
          selectedWidgets,
        },
        { headers }
      );

      if (response.status === 200 || 201) {
        setShowSuccessPopup({
          type: "success",
          show: true,
          message: "Widgets Updated Successfully"
        });
      }
      setSelectedWidgets(response.data.preferences.selectedWidgets);
      setLoading(false);
    } catch (error) {
      console.error("Error saving widgets:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    fetchAllowedWidgets([]);
    setSelectedWidgets([]);
  };

  const handleBackToUserPreference = () => {
    navigate("/user-preference");
  };

  return (
    <div className="container-fluid">
      {isLoading && <GlobalLoader />}
      <div className="row rounded theme-container mx-2">
        <div className="col-12 d-flex align-items-center justify-content-center mb-3 mt-2">
          <div className="col-6 fnt16 fw-500">{pageTitle}</div>
          <div className="col-6 d-flex justify-content-end">
            <button
              type="submit"
              className="btn btn-primary py-2 me-2 px-3 fw-500 d-flex justify-content-center align-items-center"
              onClick={handleBackToUserPreference}
            >
              Back
            </button>
            <button
              type="submit"
              className="btn btn-primary py-2 me-2 px-3 fw-500 d-flex justify-content-center align-items-center"
              onClick={handleReset}
            >
              Reset
            </button>
            <button
              type="submit"
              className="btn btn-primary py-2 px-3 fw-500 d-flex justify-content-center align-items-center"
              onClick={handleSave}
            >
              Save
            </button>
          </div>
        </div>
        {/* All Widgets */}
        <div className="col-md-6">
          <div className="widgets-box custom-shadow rounded">
            <div className="card-header border-0 px-3 py-3">
              <div className="card-title align-items-center w-100 justify-content-between d-flex">
                <span className="card-label fw-bold">
                  All Widgets
                </span>
                <span className="badge badge-light-success fw-500 fnt8 px-2 py-1 ms-2">
                  Drag desired widgets below to right and save
                </span>
              </div>
              {errorMessage && (
                <div className="pt-4 d-flex justify-content-center align-items-center text-center fnt14">No widgets found. For widgets assistance, kindly reach out to the admin.</div>
              )}
            </div>
            <ReactSortable
              tag="ul"
              className="connectedSortable p-0"
              group="widgets"
              list={allWidgets && allWidgets}
              setList={setAllWidgets}
              style={{ minHeight: "calc(100vh - 300px)" }}
            >
              {allWidgets?.map(renderWidgetCard)}
            </ReactSortable>
          </div>
        </div>

        {/* Selected Widgets */}
        <div className="col-md-6">
          <div className="widgets-box custom-shadow rounded">
            <div className="card-header border-0 px-3 py-3">
              <div className="card-title align-items-center w-100 justify-content-between d-flex">
                <span className="card-label fw-bold">Selected Widgets</span>
              </div>
            </div>
            <ReactSortable
              tag="ul"
              className="connectedSortable p-0"
              group="widgets"
              list={selectedWidgets}
              setList={setSelectedWidgets}
              style={{ minHeight: "calc(100vh - 300px)" }}
            >
              {selectedWidgets?.map(renderWidgetCard)}
            </ReactSortable>
          </div>
        </div>
      </div>
      <SuccessPopup
        type={showSuccessPopup?.type}
        show={showSuccessPopup?.show}
        message={showSuccessPopup?.message}
        onClose={() => setShowSuccessPopup(false)}
      />
    </div>
  );
};

export default UserPreferenceWidgets;